import { useEffect } from "react";
import { BRAND } from "@/lib/site";
import useT, { localize, stripLang } from "@/i18n";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setAlternate = (hreflang, href) => {
  let el = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function useSEO({ title, description, path, robots = "index, follow", jsonLd = [], type = "website", alternates = true }) {
  const { lang, t } = useT();
  const ldKey = JSON.stringify(jsonLd);
  useEffect(() => {
    const o = window.location.origin;
    const base = stripLang(path);
    const url = o + localize(base, lang);
    const image = o + "/og-image.png";
    document.documentElement.lang = t.htmlLang;
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => alternates || n.remove());
    if (alternates) {
      setAlternate("en-IN", o + base);
      setAlternate("hi-IN", o + localize(base, "hi"));
      setAlternate("x-default", o + base);
    }
    setMeta("property", "og:locale", lang === "hi" ? "hi_IN" : "en_IN");
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setCanonical(url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", BRAND);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    document.head.querySelectorAll('script[data-seo="page"]').forEach((n) => n.remove());
    JSON.parse(ldKey).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "page";
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, path, robots, type, ldKey, lang, t, alternates]);
}
